import React from "react";
import {
  Database,
  Search,
  RefreshCw,
  Server,
  ArrowRightCircle,
} from "lucide-react";

// System options for source system selection
export const systemOptions = [
  {
    id: "oracle",
    name: "Oracle JD Edwards",
    icon: <Database className="mr-2" size={18} />,
  },
  { id: "sap", name: "SAP", icon: <Database className="mr-2" size={18} /> },
  {
    id: "peoplesoft",
    name: "PeopleSoft",
    icon: <Database className="mr-2" size={18} />,
  },
  {
    id: "other",
    name: "Other Legacy System",
    icon: <Database className="mr-2" size={18} />,
  },
];

// Module options for modules overview
export const modules = [
  {
    id: "discovery",
    title: "Discovery Dashboard",
    description: "AI-powered analysis of current systems",
    icon: <Search size={24} className="text-blue-500" />,
  },
  {
    id: "mapping",
    title: "Data Mapping Visualizer",
    description: "Intelligent mapping from legacy to Workday",
    icon: <Database size={24} className="text-green-500" />,
  },
  {
    id: "learning",
    title: "Learning Model Demonstration",
    description: "See how our AI improves with each implementation",
    icon: <RefreshCw size={24} className="text-purple-500" />,
  },
  {
    id: "integration",
    title: "Workday Integration Simulator",
    description: "Preview data in Workday format before migration",
    icon: <Server size={24} className="text-orange-500" />,
  },
  {
    id: "timeline",
    title: "Timeline Comparison Tool",
    description: "Compare traditional vs. Nexus implementation timelines",
    icon: <ArrowRightCircle size={24} className="text-red-500" />,
  },
];

// Configuration setup sections
export const configSetupSections = [
  {
    id: "employeeTypes",
    title: "Employee Types",
    examples: [
      "Regular",
      "Temporary",
      "Seasonal",
      "Fixed Term",
      "Intern",
      "Student",
      "Casual",
    ],
  },
  {
    id: "cwTypes",
    title: "CW Types",
    examples: ["Board Member", "Consultant", "Contractor"],
  },
  {
    id: "payRateTypes",
    title: "Pay Rate Types",
    examples: ["Salary", "Hourly"],
  },
  { id: "timeProfiles", title: "Time Profiles", examples: ["37.5", "40"] },
  {
    id: "licenseIdTypes",
    title: "License ID Types",
    examples: ["Drivers' License", "CPR License"],
  },
  {
    id: "locationTypes",
    title: "Location Types",
    examples: [
      "Administrative Facility",
      "Building",
      "Headquarters",
      "Office Location",
      "Manufacturing Facility",
    ],
  },
  {
    id: "locationUsages",
    title: "Location Usages",
    examples: ["Business Site", "Industrial Site"],
  },
  {
    id: "terminationReasons",
    title: "Termination Reasons",
    examples: [
      "Voluntary",
      "Dissatisfied with Job",
      "Other Employment",
      "Dissatisfied with Management",
      "Dissatisfied with Pay",
      "Involuntary",
      "Harassment",
      "Death",
      "Not Authorized to Work",
      "Poor Job Performance",
    ],
  },
  {
    id: "managementLevels",
    title: "Management Levels",
    examples: [
      "Board of Directors",
      "Chief Executive Officer",
      "Executive Vice President",
      "Senior Vice President",
      "Vice President",
      "Director",
      "Manager",
      "Supervisor",
      "Individual Contributor",
    ],
  },
  {
    id: "otherIds",
    title: "Other IDs",
    examples: ["ID Type (Additional Field)"],
  },
];

// Foundational setup sections
export const foundationalSetupSections = [
  {
    id: "regions",
    title: "Regions/Countries",
    addActionText: "Add Region",
    hasHierarchy: true,
  },
  { id: "companies", title: "Companies", hasAddressLookup: true },
  {
    id: "locations",
    title: "Locations",
    hasAddressLookup: true,
    hasContactInfo: true,
  },
  { id: "costCenters", title: "Cost Centers", hasHierarchy: true },
  {
    id: "departments",
    title: "Departments",
    hasHierarchy: true,
    examples: [
      "Finance",
      "Human Resources",
      "Information Technology",
      "Marketing",
      "Operations",
      "Research & Development",
      "Sales",
    ],
  },
  {
    id: "gradeProfiles",
    title: "Grade Profiles",
    examples: [
      "Entry Level",
      "Junior",
      "Mid-Level",
      "Senior",
      "Lead",
      "Principal",
      "Executive",
    ],
    hasAdditionalFields: true,
    additionalFields: [
      { name: "eligibilityRule", label: "Eligibility Rule", type: "text" },
      { name: "minimum", label: "Minimum", type: "number" },
      { name: "midpoint", label: "Midpoint", type: "number" },
      { name: "maximum", label: "Maximum", type: "number" },
      {
        name: "frequency",
        label: "Frequency",
        type: "select",
        options: ["Annual", "Hourly"],
      },
      {
        name: "country",
        label: "Country",
        type: "select",
        optionsFrom: "regions",
      },
    ],
  },
  {
    id: "jobProfiles",
    title: "Job Profiles",
    examples: [
      "Software Engineer",
      "Product Manager",
      "HR Specialist",
      "Financial Analyst",
      "Marketing Coordinator",
    ],
    hasLinkedField: true,
    linkedField: {
      name: "grade",
      label: "Grade",
      type: "select",
      optionsFrom: "gradeProfiles",
    },
  },
];

// Default configuration item template
export const defaultConfigItem = {
  legacyName: "",
  legacyCode: "",
  description: "",
  newName: "",
  newCode: "",
  newDescription: "",
  comments: "",
  action: "keep", // 'keep', 'merge', 'notRequired'
  // Additional fields for grade profiles
  eligibilityRule: "",
  minimum: "",
  midpoint: "",
  maximum: "",
  frequency: "Annual",
  country: "",
  // Additional field for job profiles
  grade: "",
};

// HCM specific configuration sections
export const hcmConfigSetupSections = [
  {
    id: "employeeTypes",
    title: "Employee Types",
    examples: ["Full Time", "Part Time", "Temporary", "Contract", "Intern"],
  },
  {
    id: "payRateTypes",
    title: "Pay Rate Types",
    examples: ["Hourly", "Salary", "Commission", "Piece Rate", "Project Based"],
  },
  {
    id: "timeProfiles",
    title: "Time Profiles",
    examples: [
      "Standard 40 Hour",
      "Part Time 20 Hour",
      "Flexible Schedule",
      "Compressed Week",
      "9/80 Schedule",
    ],
  },
  {
    id: "managementLevels",
    title: "Management Levels",
    examples: [
      "Individual Contributor",
      "Team Lead",
      "Manager",
      "Director",
      "Executive",
    ],
  },
];

// Number of questionnaire steps
export const QUESTIONNAIRE_STEPS_COUNT = 6;

// Questionnaire step titles
export const questionnaireStepTitles = [
  "Organization Profile",
  "SKU Selection",
  "Module Overview",
  "Configuration Setup",
  "Foundational Setup",
  "Review & Submit",
];

// Discovery step titles
export const discoveryStepTitles = [
  "Select Source System",
  "Connection Details",
  "Initial Questionnaire",
  "Analysis & Gap Detection",
];
